import { computed, ref, type Ref } from 'vue'

import { ACTIVE_DAYS } from '@/types/offer'
import type { ActiveDay, TimeSlot } from '@/types/offer'

export const useScheduleSlots = (timeSlots: Ref<TimeSlot[]>) => {
  const lastClickedDayIndexBySlot = ref<Record<number, number | null>>({})
  const hasTimeSlotsInteracted = ref(false)

  const hasTimeSlotsError = computed(() => {
    const slotsWithContent = timeSlots.value.filter((slot) => slot.days.length > 0 || slot.startTime || slot.endTime)

    if (slotsWithContent.length === 0) return false

    for (const slot of slotsWithContent) {
      if (slot.days.length === 0) return true
      if (!!slot.startTime !== !!slot.endTime) return true
      if (slot.startTime && slot.endTime && slot.endTime <= slot.startTime) return true
    }

    return false
  })

  const createTimeSlot = (id: number): TimeSlot => ({
    id,
    days: [...ACTIVE_DAYS],
    startTime: '',
    endTime: ''
  })

  const getDayIndex = (day: ActiveDay) => ACTIVE_DAYS.indexOf(day)

  const toggleDayForSlot = (slotId: number, day: ActiveDay, event?: MouseEvent) => {
    hasTimeSlotsInteracted.value = true
    const slot = timeSlots.value.find((s) => s.id === slotId)
    if (!slot) return

    const clickedIndex = getDayIndex(day)
    if (clickedIndex === -1) return

    const isShiftClick = event?.shiftKey
    const lastIndex = lastClickedDayIndexBySlot.value[slotId] ?? null

    if (isShiftClick && lastIndex !== null && lastIndex !== clickedIndex) {
      const start = Math.min(lastIndex, clickedIndex)
      const end = Math.max(lastIndex, clickedIndex)

      const rangeDays = ACTIVE_DAYS.slice(start, end + 1)
      const uniqueDays = new Set<ActiveDay>([...slot.days, ...rangeDays])
      slot.days = Array.from(uniqueDays)
    } else {
      if (slot.days.includes(day)) {
        slot.days = slot.days.filter((d) => d !== day)
      } else {
        slot.days = [...slot.days, day]
      }
    }

    lastClickedDayIndexBySlot.value[slotId] = clickedIndex
  }

  const addTimeSlot = () => {
    const nextId = (timeSlots.value[timeSlots.value.length - 1]?.id ?? 0) + 1
    timeSlots.value.push(createTimeSlot(nextId))
  }

  const removeTimeSlot = (slotId: number) => {
    if (timeSlots.value.length <= 1) return
    timeSlots.value = timeSlots.value.filter((s) => s.id !== slotId)
  }

  const duplicateTimeSlot = (slotId: number) => {
    const source = timeSlots.value.find((s) => s.id === slotId)
    if (!source) return

    const nextId = (timeSlots.value[timeSlots.value.length - 1]?.id ?? 0) + 1
    const clone: TimeSlot = {
      id: nextId,
      days: [...source.days],
      startTime: source.startTime,
      endTime: source.endTime
    }

    timeSlots.value.push(clone)
  }

  return {
    lastClickedDayIndexBySlot,
    hasTimeSlotsInteracted,
    hasTimeSlotsError,
    createTimeSlot,
    getDayIndex,
    toggleDayForSlot,
    addTimeSlot,
    removeTimeSlot,
    duplicateTimeSlot
  }
}
